import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, Save, CheckCircle2, AlertTriangle } from "lucide-react";

const getDisplayValue = (value) => {
  if (value && typeof value === "object" && "value" in value) {
    return value.value;
  }
  return value ?? "";
};

const INITIAL = {
  eigentuemer_name: "",
  "1_rueckrufnummer": "",
  "1_beziehung_zum_objekt": "",
  kontakt_email: "",
  eigentuemer_grundbuch: "",
  eigentuemer_seit: null,
  "2_objektart": "",
  "2_adresse": "",
  "2_plz": "",
  "2_stadt": "",
  objekt_hypothek_bank: "",
  objekt_wohnflaeche_qm: "",
  objekt_grundstueck_qm: "",
  objekt_zimmer: "",
  objekt_baeder: "",
  objekt_selbstbewohnt: false,
  "3_preisvorstellung": "",
  "3_vermietet": false,
  "3_monatliche_nettokaltmiete": "",
  makler_aktiv: false,
  makler_name: "",
  makler_adresse: "",
  makler_plz: "",
  makler_ort: "",
  makler_telefon: "",
  "4_zustand": "",
  "4_modernisierung": "",
  "4_reparaturkosten": "",
  "5_verkaufsgrund": "",
  "5_verkaufsdruck": "",
  "6_verkaufszeitraum": "",
  "7_lage_einschaetzung": "",
  "7_besonderheiten": "",
  "7_besichtigung_empfohlen": false,
  preis_qm_durchschnitt: null,
  "8_KO_kriterium": "",
  manuelle_entscheidung: "",
  "9_besichtigungstermin": null,
  "9_besichtigungstermin_zeit": "",
  "9_nachkontakt_abgeschlossen": false,
  "9_nachkontakt_notiz": "",
  lead_outcome: "offen",
  last_updated_at: null,
};

const HOURS = Array.from({ length: 14 }, (_, i) => String(i + 7).padStart(2, "0"));
const MINUTES = ["00", "15", "30", "45"];

function validatePlz(plz) {
  if (!plz) return null;
  if (!/^\d+$/.test(plz)) return "Nur Ziffern erlaubt";
  if (plz.length !== 5) return "PLZ muss 5-stellig sein";
  return null;
}

function addressWarning(form) {
  const hasPlz = form["2_plz"]?.length > 0;
  const hasStadt = form["2_stadt"]?.length > 0;
  const hasAdresse = form["2_adresse"]?.length > 0;
  if ((hasPlz || hasStadt || hasAdresse) && !(hasPlz && hasStadt && hasAdresse)) {
    return "Adresse unvollstaendig - bitte alle Felder ausfuellen";
  }
  return null;
}

function validateEmail(email) {
  if (!email) return null;
  if (!email.includes("@")) return "E-Mail muss ein @ enthalten";
  const parts = email.split("@");
  if (parts.length !== 2 || !parts[1].includes(".")) return "Ungueltiges E-Mail-Format";
  return null;
}

export function calculateLeadScore(sheet) {
  if (!sheet) return { pct: 0, label: "Keine Daten", level: "none" };
  // KO-Kriterium → immediate hard 0%
  if (getDisplayValue(sheet["8_KO_kriterium"]) !== "") {
    return { pct: 0, label: "Nicht weiterverfolgen", level: "red" };
  }
  let score = 0;
  let max = 0;
  const add = (pts, maxPts) => { score += pts; max += maxPts; };

  const verkaufsdruck = getDisplayValue(sheet["5_verkaufsdruck"]).toLowerCase();
  if (verkaufsdruck === "hoch") add(3, 3);
  else if (verkaufsdruck === "mittel") add(2, 3);
  else if (verkaufsdruck === "niedrig") add(1, 3);

  const verkaufszeitraum = getDisplayValue(sheet["6_verkaufszeitraum"]).toLowerCase();
  if (verkaufszeitraum === "sofort") add(3, 3);
  else if (verkaufszeitraum === "3 monate") add(2, 3);
  else if (verkaufszeitraum === "6 monate") add(1, 3);
  else add(0, 3);

  if (getDisplayValue(sheet["3_preisvorstellung"])) add(2, 2); else add(0, 2);

  const lage = getDisplayValue(sheet["7_lage_einschaetzung"]).toLowerCase();
  if (lage === "sehr gut") add(3, 3);
  else if (lage === "gut") add(2, 3);
  else if (lage === "mittel") add(1, 3);
  else add(0, 3);

  const zust = getDisplayValue(sheet["4_zustand"]).toLowerCase();
  if (zust === "sehr gut" || zust === "gut") add(2, 2);
  else if (zust === "mittel") add(1, 2);
  else add(0, 2);

  if (getDisplayValue(sheet["2_objektart"])) add(1, 1); else add(0, 1);
  const pct = max > 0 ? Math.round((score / max) * 100) : 0;
  if (pct >= 60) return { pct, label: "Termin empfohlen", level: "green" };
  if (pct >= 30) return { pct, label: "Pruefen", level: "yellow" };
  return { pct, label: "Niedrige Prioritaet", level: "red" };
}

// List of fields that may come as Baserow single-select objects
const SINGLE_SELECT_FIELDS = [
  "1_beziehung_zum_objekt",
  "2_objektart",
  "4_zustand",
  "5_verkaufsdruck",
  "6_verkaufszeitraum",
  "7_lage_einschaetzung",
  "8_KO_kriterium",
  "manuelle_entscheidung",
  "lead_outcome",
];

export default function SellerSheetForm({
  data,
  onSave,
  onComplete,
  saving,
  onFormChange,
}) {
  const [form, setForm] = useState(INITIAL);
  const [eigentuemerSeitInput, setEigentuemerSeitInput] = useState("");

  useEffect(() => {
    if (!form.eigentuemer_seit) {
      setEigentuemerSeitInput("");
      return;
    }

    const dateStr = form.eigentuemer_seit;
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const [y, m, d] = dateStr.split("T")[0].split("-");
      setEigentuemerSeitInput(`${d}.${m}.${y}`);
    }
  }, [form.eigentuemer_seit]);

  useEffect(() => {
    if (data) {
      setForm((prev) => {
        const merged = { ...prev };
        for (const key of Object.keys(prev)) {
          if (data[key] !== undefined && data[key] !== null) {
            // Normalize Baserow single-select objects to plain strings
            if (SINGLE_SELECT_FIELDS.includes(key)) {
              merged[key] = getDisplayValue(data[key]);
            } else {
              merged[key] = data[key];
            }
          }
        }
        if (
          merged["3_preisvorstellung"] !== null &&
          merged["3_preisvorstellung"] !== ""
        ) {
          merged["3_preisvorstellung"] = String(merged["3_preisvorstellung"]);
        }
        if (
          merged["3_monatliche_nettokaltmiete"] !== null &&
          merged["3_monatliche_nettokaltmiete"] !== ""
        ) {
          merged["3_monatliche_nettokaltmiete"] = String(merged["3_monatliche_nettokaltmiete"]);
        }
        if (
          merged["4_reparaturkosten"] !== null &&
          merged["4_reparaturkosten"] !== ""
        ) {
          merged["4_reparaturkosten"] = String(merged["4_reparaturkosten"]);
        }
        // Convert all numeric fields to string for input display
        for (const numKey of ["objekt_wohnflaeche_qm", "objekt_grundstueck_qm", "objekt_zimmer", "objekt_baeder"]) {
          if (merged[numKey] !== null && merged[numKey] !== "" && merged[numKey] !== undefined) {
            merged[numKey] = String(merged[numKey]);
          }
        }
        return merged;
      });
    }
  }, [data]);

  useEffect(() => {
    if (onFormChange) onFormChange(form);
  }, [form, onFormChange]);

useEffect(() => {
  if (form.eigentuemer_seit) {
    try {
      const [y, m, d] = form.eigentuemer_seit.split("T")[0].split("-");
      setEigentuemerSeitInput(`${d}.${m}.${y}`);
    } catch {
      setEigentuemerSeitInput("");
    }
  }
}, [form.eigentuemer_seit]);

  const set = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const getPayload = () => {
    const p = { ...form };
    const toNum = (k) => { p[k] = p[k] ? Number(p[k]) : null; };
    toNum("3_preisvorstellung");
    toNum("3_monatliche_nettokaltmiete");
    toNum("4_reparaturkosten");
    toNum("objekt_wohnflaeche_qm");
    toNum("objekt_grundstueck_qm");
    toNum("objekt_zimmer");
    toNum("objekt_baeder");
    // Strip read-only / server-set fields from payload
    delete p.last_updated_at;
    delete p.preis_qm_durchschnitt;
    delete p.score_preis;
    delete p.score_zustand;
    delete p.score_verkaufsdruck;
    delete p.score_verkaufszeitraum;
    delete p.score_motivation;
    delete p.score_beziehung;
    delete p.score_lage;
    delete p.score_gesamt;
    delete p.entscheidung;
    return p;
  };

  const plzError = useMemo(() => validatePlz(form["2_plz"]), [form]);
  const addrWarn = useMemo(() => addressWarning(form), [form]);
  const emailError = useMemo(() => validateEmail(form.kontakt_email), [form]);
  // Check for owner relationship - accept both umlaut and ASCII variants
  const beziehungValue = getDisplayValue(form["1_beziehung_zum_objekt"]);
  const isEigentuemer = beziehungValue === "Eigentümer" || beziehungValue === "Eigentuemer";

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable Form */}
      <div className="flex-1 overflow-y-auto p-8 pb-4 bg-[#F8F9FA]">
        <h2 className="text-xl font-semibold tracking-tight text-[#1C1F22] mb-2">
          Verkaeuferdatenblatt
        </h2>

        {/* Lead Outcome - separate from automated status */}
        <div className="mb-6 bg-white rounded-lg p-4 border border-[#E2E4E9] shadow-sm">
          <Field label="Ergebnis (manuell)">
            <ToggleGroup
              type="single"
              value={getDisplayValue(form.lead_outcome)}
              onValueChange={(v) => { if (v) set("lead_outcome", v); }}
              className="justify-start gap-2 flex-wrap"
              data-testid="field-lead-outcome-group"
            >
              {[
                { v: "offen", l: "Offen", c: "data-[state=on]:bg-[#5E646A] data-[state=on]:text-white data-[state=on]:border-[#5E646A]" },
                { v: "deal", l: "Deal", c: "data-[state=on]:bg-emerald-600 data-[state=on]:text-white data-[state=on]:border-emerald-600" },
                { v: "no_deal", l: "No Deal", c: "data-[state=on]:bg-orange-600 data-[state=on]:text-white data-[state=on]:border-orange-600" },
                { v: "abgelehnt", l: "Abgelehnt", c: "data-[state=on]:bg-red-600 data-[state=on]:text-white data-[state=on]:border-red-600" },
                { v: "vermittelt", l: "Vermittlung", c: "data-[state=on]:bg-blue-600 data-[state=on]:text-white data-[state=on]:border-blue-600" },
              ].map((opt) => (
                <ToggleGroupItem
                  key={opt.v}
                  value={opt.v}
                  className={`px-4 py-2 text-sm rounded-md border border-[#E2E4E9] hover:bg-[#F0F2F5] ${opt.c}`}
                  data-testid={`field-outcome-${opt.v}`}
                >
                  {opt.l}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </Field>
{getDisplayValue(form.lead_outcome) !== "offen" && (
  <p className="text-xs text-[#0A4F53] font-medium mt-2 flex items-center gap-1" data-testid="outcome-completed-hint">
    Ergebnis gesetzt: {{
      offen: "Offen",
      deal: "Deal",
      no_deal: "No Deal",
      abgelehnt: "Abgelehnt",
      vermittelt: "Vermittlung",
    }[getDisplayValue(form.lead_outcome)] || getDisplayValue(form.lead_outcome)}
  </p>
)}
        </div>

        {/* 1. Ansprechpartner */}
        <Section num="1" title="Ansprechpartner">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Eigentuemer / Name" htmlFor="eigentuemer_name">
              <Input
                id="eigentuemer_name"
                value={form.eigentuemer_name}
                onChange={(e) => set("eigentuemer_name", e.target.value)}
                placeholder="Name des Eigentuemers"
                data-testid="field-eigentuemer-name"
              />
            </Field>
            <Field label="Rueckrufnummer" htmlFor="rueckrufnummer">
              <Input
                id="rueckrufnummer"
                value={form["1_rueckrufnummer"]}
                onChange={(e) => set("1_rueckrufnummer", e.target.value)}
                placeholder="+49 ..."
                data-testid="field-rueckrufnummer"
              />
            </Field>
          </div>
          <div>
            <Field label="E-Mail" htmlFor="kontakt_email">
              <Input
                id="kontakt_email"
                type="email"
                value={form.kontakt_email}
                onChange={(e) => set("kontakt_email", e.target.value)}
                placeholder="name@beispiel.de"
                className={emailError ? "border-red-400 focus:border-red-500 focus:ring-red-400/20" : ""}
                data-testid="field-kontakt-email"
              />
            </Field>
            {emailError && (
              <p className="text-xs text-red-500 mt-1" data-testid="email-error">{emailError}</p>
            )}
          </div>
<Field label="Beziehung zum Objekt" htmlFor="beziehung">
  <Select
    value={getDisplayValue(form["1_beziehung_zum_objekt"])}
    onValueChange={(v) => set("1_beziehung_zum_objekt", v)}
  >
    <SelectTrigger data-testid="field-beziehung-trigger">
      <SelectValue placeholder="Auswaehlen..." />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="Eigentümer">Eigentümer</SelectItem>
      <SelectItem value="Erbe">Erbe</SelectItem>
      <SelectItem value="Bevollmächtigter">Bevollmächtigter</SelectItem>
      <SelectItem value="Makler">Makler</SelectItem>
      <SelectItem value="Sonstige">Sonstige</SelectItem>
    </SelectContent>
  </Select>
</Field>
          {isEigentuemer && (
            <div className="border-t border-[#E2E4E9] pt-4 mt-1 space-y-4">
              <Field label="Grundbuch-Eintrag" htmlFor="eigentuemer_grundbuch">
                <Textarea
                  id="eigentuemer_grundbuch"
                  value={form.eigentuemer_grundbuch}
                  onChange={(e) => set("eigentuemer_grundbuch", e.target.value)}
                  placeholder="Grundbuch-Informationen..."
                  rows={2}
                  data-testid="field-eigentuemer-grundbuch"
                />
              </Field>
              <Field label="Eigentuemer seit" htmlFor="eigentuemer_seit">
                <div className="flex gap-2 items-center max-w-xs">
		<Input
  		id="eigentuemer_seit_manual"
  		placeholder="TT.MM.JJJJ"
  		className="w-32"
  		value={eigentuemerSeitInput}
  		onChange={(e) => {
    		const val = e.target.value;
    		setEigentuemerSeitInput(val);

    		const match = val.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    		if (match) {
      		const [, d, m, y] = match;
      		const day = d.padStart(2, "0");
      		const month = m.padStart(2, "0");
      		set("eigentuemer_seit", `${y}-${month}-${day}`);
    		} else if (!val) {
      		set("eigentuemer_seit", null);
    		}
  		}}
		data-testid="field-eigentuemer-seit-input"
		/>	
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="shrink-0 border-[#E2E4E9]"
                        data-testid="field-eigentuemer-seit-trigger"
                      >
                        <CalendarIcon className="h-4 w-4 text-[#5E646A]" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.eigentuemer_seit ? (() => {
                          try {
                            const dateStr = form.eigentuemer_seit;
                            if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
                              const [y, m, d] = dateStr.split("T")[0].split("-").map(Number);
                              return new Date(y, m - 1, d);
                            }
                            return new Date(dateStr);
                          } catch {
                            return undefined;
                          }
                        })() : undefined}
                        onSelect={(date) => {
                          if (date) {
                            // Store as ISO YYYY-MM-DD (local date, no timezone shift)
                            const y = date.getFullYear();
                            const m = String(date.getMonth() + 1).padStart(2, "0");
                            const d = String(date.getDate()).padStart(2, "0");
                            set("eigentuemer_seit", `${y}-${m}-${d}`);
                          } else {
                            set("eigentuemer_seit", null);
                          }
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </Field>
            </div>
          )}
        </Section>

        {/* 2. Objekt - with address validation + extended fields */}
        <Section num="2" title="Objekt">
          <Field label="Objektart" htmlFor="objektart">
            <Select
              value={getDisplayValue(form["2_objektart"])}
              onValueChange={(v) => set("2_objektart", v)}
            >
              <SelectTrigger data-testid="field-objektart-trigger">
                <SelectValue placeholder="Auswaehlen..." />
              </SelectTrigger>
		<SelectContent>
  		<SelectItem value="ETW">Eigentumswohnung</SelectItem>
  		<SelectItem value="MFH">Mehrfamilienhaus</SelectItem>
  		<SelectItem value="EFH">Einfamilienhaus</SelectItem>
  		<SelectItem value="DHH">Doppelhaushälfte</SelectItem>
  		<SelectItem value="RH">Reihenhaus</SelectItem>
  		<SelectItem value="RMH">Reihenmittelhaus</SelectItem>
  		<SelectItem value="REH">Reihenendhaus</SelectItem>
  		<SelectItem value="Grundstück">Grundstück</SelectItem>
	 	<SelectItem value="Sonstiges">Sonstiges</SelectItem>
		</SelectContent>
            </Select>
          </Field>
          <Field label="Adresse" htmlFor="adresse">
            <Input
              id="adresse"
              value={form["2_adresse"]}
              onChange={(e) => set("2_adresse", e.target.value)}
              placeholder="Strasse und Hausnummer"
              className={addrWarn && !form["2_adresse"] ? "border-amber-400 focus:border-amber-500 focus:ring-amber-400/20" : ""}
              data-testid="field-adresse"
            />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Field label="PLZ" htmlFor="plz">
                <Input
                  id="plz"
                  value={form["2_plz"]}
                  onChange={(e) => set("2_plz", e.target.value.replace(/[^\d]/g, "").slice(0, 5))}
                  placeholder="z.B. 80331"
                  maxLength={5}
                  className={plzError ? "border-red-400 focus:border-red-500 focus:ring-red-400/20" : addrWarn && !form["2_plz"] ? "border-amber-400 focus:border-amber-500 focus:ring-amber-400/20" : ""}
                  data-testid="field-plz"
                />
              </Field>
              {plzError && (
                <p className="text-xs text-red-500 mt-1" data-testid="plz-error">{plzError}</p>
              )}
            </div>
            <div className="col-span-2">
              <Field label="Stadt" htmlFor="stadt">
                <Input
                  id="stadt"
                  value={form["2_stadt"]}
                  onChange={(e) => set("2_stadt", e.target.value)}
                  placeholder="Stadt"
                  className={addrWarn && !form["2_stadt"] ? "border-amber-400 focus:border-amber-500 focus:ring-amber-400/20" : ""}
                  data-testid="field-stadt"
                />
              </Field>
            </div>
          </div>
          {addrWarn && (
            <div className="flex items-center gap-2 text-amber-600 text-xs mt-1" data-testid="address-warning">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {addrWarn}
            </div>
          )}
          <div className="border-t border-[#E2E4E9] pt-4 mt-1">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Wohnflaeche" htmlFor="wohnflaeche">
                <div className="relative">
                  <Input
                    id="wohnflaeche"
                    type="text"
                    inputMode="numeric"
                    value={form.objekt_wohnflaeche_qm}
                    onChange={(e) => set("objekt_wohnflaeche_qm", e.target.value.replace(/[^\d]/g, ""))}
                    placeholder="z.B. 120"
                    data-testid="field-wohnflaeche"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#5E646A]">qm</span>
                </div>
              </Field>
              <Field label="Grundstueck" htmlFor="grundstueck">
                <div className="relative">
                  <Input
                    id="grundstueck"
                    type="text"
                    inputMode="numeric"
                    value={form.objekt_grundstueck_qm}
                    onChange={(e) => set("objekt_grundstueck_qm", e.target.value.replace(/[^\d]/g, ""))}
                    placeholder="z.B. 500"
                    data-testid="field-grundstueck"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#5E646A]">qm</span>
                </div>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Field label="Zimmer" htmlFor="zimmer">
                <Input
                  id="zimmer"
                  type="text"
                  inputMode="numeric"
                  value={form.objekt_zimmer}
                  onChange={(e) => set("objekt_zimmer", e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="Anzahl"
                  data-testid="field-zimmer"
                />
              </Field>
              <Field label="Baeder" htmlFor="baeder">
                <Input
                  id="baeder"
                  type="text"
                  inputMode="numeric"
                  value={form.objekt_baeder}
                  onChange={(e) => set("objekt_baeder", e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="Anzahl"
                  data-testid="field-baeder"
                />
              </Field>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <Checkbox
                id="selbstbewohnt"
                checked={form.objekt_selbstbewohnt}
                onCheckedChange={(v) => set("objekt_selbstbewohnt", Boolean(v))}
                data-testid="field-selbstbewohnt"
              />
              <Label htmlFor="selbstbewohnt" className="text-sm font-medium text-[#1C1F22] cursor-pointer">
                Selbstbewohnt
              </Label>
            </div>
          </div>
          <Field label="Hypothek / Bank" htmlFor="hypothek_bank">
            <Input
              id="hypothek_bank"
              value={form.objekt_hypothek_bank}
              onChange={(e) => set("objekt_hypothek_bank", e.target.value)}
              placeholder="Name der Bank / Hypothekendetails"
              data-testid="field-hypothek-bank"
            />
          </Field>
        </Section>

        {/* 3. Preis & Vermietung - with monthly rent + makler */}
        <Section num="3" title="Preis & Vermietung">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Preisvorstellung" htmlFor="preis">
              <div className="relative">
                <Input
                  id="preis"
                  type="text"
                  inputMode="numeric"
                  value={form["3_preisvorstellung"]}
                  onChange={(e) =>
                    set("3_preisvorstellung", e.target.value.replace(/[^\d]/g, ""))
                  }
                  placeholder="z.B. 350000"
                  data-testid="field-preisvorstellung"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#5E646A]">EUR</span>
              </div>
            </Field>
            <div className="flex items-center gap-3 pt-6">
              <Checkbox
                id="vermietet"
                checked={form["3_vermietet"]}
                onCheckedChange={(v) => set("3_vermietet", Boolean(v))}
                data-testid="field-vermietet"
              />
              <Label htmlFor="vermietet" className="text-sm font-medium text-[#1C1F22] cursor-pointer">
                Aktuell vermietet
              </Label>
            </div>
          </div>
          <Field label="Monatliche Nettokaltmiete" htmlFor="nettokaltmiete">
            <div className="relative max-w-xs">
              <Input
                id="nettokaltmiete"
                type="text"
                inputMode="numeric"
                value={form["3_monatliche_nettokaltmiete"]}
                onChange={(e) =>
                  set("3_monatliche_nettokaltmiete", e.target.value.replace(/[^\d]/g, ""))
                }
                placeholder="z.B. 1200"
                data-testid="field-nettokaltmiete"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#5E646A]">EUR/Monat</span>
            </div>
          </Field>
          <Field label="Durchschnittlicher qm Preis">
            <p
              className="text-sm font-medium text-[#1C1F22] py-2 px-3 bg-[#F8F9FA] rounded border border-[#E2E4E9] max-w-xs"
              data-testid="field-preis-qm-durchschnitt"
            >
              {form.preis_qm_durchschnitt != null
                ? `${Number(form.preis_qm_durchschnitt).toLocaleString("de-DE")} EUR/qm`
                : "-"}
            </p>
          </Field>
          <div className="border-t border-[#E2E4E9] pt-4 mt-1">
            <div className="flex items-center gap-3">
              <Checkbox
                id="makler_aktiv"
                checked={form.makler_aktiv}
                onCheckedChange={(v) => set("makler_aktiv", Boolean(v))}
                data-testid="field-makler-aktiv"
              />
              <Label htmlFor="makler_aktiv" className="text-sm font-medium text-[#1C1F22] cursor-pointer">
                Makler aktiv
              </Label>
            </div>
            {form.makler_aktiv && (
              <div className="mt-4 space-y-4 pl-7">
                <Field label="Makler Name" htmlFor="makler_name">
                  <Input
                    id="makler_name"
                    value={form.makler_name}
                    onChange={(e) => set("makler_name", e.target.value)}
                    placeholder="Maklername / Firma"
                    data-testid="field-makler-name"
                  />
                </Field>
                <Field label="Makler Adresse" htmlFor="makler_adresse">
                  <Input
                    id="makler_adresse"
                    value={form.makler_adresse}
                    onChange={(e) => set("makler_adresse", e.target.value)}
                    placeholder="Strasse"
                    data-testid="field-makler-adresse"
                  />
                </Field>
                <div className="grid grid-cols-3 gap-4">
                  <Field label="PLZ" htmlFor="makler_plz">
                    <Input
                      id="makler_plz"
                      value={form.makler_plz}
                      onChange={(e) => set("makler_plz", e.target.value.replace(/[^\d]/g, "").slice(0, 5))}
                      placeholder="PLZ"
                      maxLength={5}
                      data-testid="field-makler-plz"
                    />
                  </Field>
                  <div className="col-span-2">
                    <Field label="Ort" htmlFor="makler_ort">
                      <Input
                        id="makler_ort"
                        value={form.makler_ort}
                        onChange={(e) => set("makler_ort", e.target.value)}
                        placeholder="Ort"
                        data-testid="field-makler-ort"
                      />
                    </Field>
                  </div>
                </div>
                <Field label="Telefon" htmlFor="makler_telefon">
                  <Input
                    id="makler_telefon"
                    value={form.makler_telefon}
                    onChange={(e) => set("makler_telefon", e.target.value)}
                    placeholder="+49 ..."
                    data-testid="field-makler-telefon"
                  />
                </Field>
              </div>
            )}
          </div>
        </Section>

        {/* 4. Zustand - updated options */}
        <Section num="4" title="Zustand">
          <Field label="Zustand">
            <ToggleGroup
              type="single"
              value={getDisplayValue(form["4_zustand"])}
              onValueChange={(v) => set("4_zustand", v)}
              className="justify-start gap-2 flex-wrap"
              data-testid="field-zustand-group"
            >
{[
  { value: "sehr gut", label: "Sehr gut" },
  { value: "gut", label: "Gut" },
  { value: "mittel", label: "Mittel" },
  { value: "schlecht", label: "Schlecht" },
  { value: "kernsanierung", label: "Kernsanierung" },
].map((opt) => (
  <ToggleGroupItem
    key={opt.value}
    value={opt.value}
    className="px-4 py-2 text-sm rounded-md border border-[#E2E4E9] data-[state=on]:bg-[#0A4F53] data-[state=on]:text-white"
    data-testid={`field-zustand-${opt.value.replace(/\s/g, "-")}`}
  >
    {opt.label}
  </ToggleGroupItem>
))}
            </ToggleGroup>
          </Field>
          <Field label="Letzte / notwendige Modernisierungen, Renovierungen" htmlFor="modernisierung">
            <Textarea
              id="modernisierung"
              value={form["4_modernisierung"]}
              onChange={(e) => set("4_modernisierung", e.target.value)}
              placeholder="Beschreibung der letzten oder notwendigen Massnahmen..."
              rows={3}
              data-testid="field-modernisierung"
            />
          </Field>
          <Field label="Reparaturkosten ca." htmlFor="reparaturkosten">
            <div className="relative max-w-xs">
              <Input
                id="reparaturkosten"
                type="text"
                inputMode="numeric"
                value={form["4_reparaturkosten"]}
                onChange={(e) =>
                  set("4_reparaturkosten", e.target.value.replace(/[^\d]/g, ""))
                }
                placeholder="z.B. 50000"
                data-testid="field-reparaturkosten"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#5E646A]">EUR</span>
            </div>
          </Field>
        </Section>

        {/* 5. Motivation */}
        <Section num="5" title="Motivation">
          <Field label="Verkaufsgrund" htmlFor="verkaufsgrund">
            <Textarea
              id="verkaufsgrund"
              value={form["5_verkaufsgrund"]}
              onChange={(e) => set("5_verkaufsgrund", e.target.value)}
              placeholder="Warum wird verkauft?"
              rows={3}
              data-testid="field-verkaufsgrund"
            />
          </Field>
          <Field label="Verkaufsdruck">
            <ToggleGroup
              type="single"
              value={getDisplayValue(form["5_verkaufsdruck"])}
              onValueChange={(v) => set("5_verkaufsdruck", v)}
              className="justify-start gap-2"
              data-testid="field-verkaufsdruck-group"
            >
	{[
  { value: "niedrig", label: "Niedrig" },
  { value: "mittel", label: "Mittel" },
  { value: "hoch", label: "Hoch" },
].map((opt) => (
  <ToggleGroupItem
    key={opt.value}
    value={opt.value}
    className="px-4 py-2 text-sm rounded-md border border-[#E2E4E9] data-[state=on]:bg-[#0A4F53] data-[state=on]:text-white"
    data-testid={`field-verkaufsdruck-${opt.value}`}
  >
    {opt.label}
  </ToggleGroupItem>
))}	
            </ToggleGroup>
          </Field>
        </Section>

        {/* 6. Zeitraum */}
        <Section num="6" title="Zeitraum">
          <Field label="Verkaufszeitraum">
            <Select
              value={getDisplayValue(form["6_verkaufszeitraum"])}
              onValueChange={(v) => set("6_verkaufszeitraum", v)}
            >
              <SelectTrigger data-testid="field-verkaufszeitraum-trigger">
                <SelectValue placeholder="Auswaehlen..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sofort">Sofort</SelectItem>
                <SelectItem value="3 Monate">1–3 Monate</SelectItem>
                <SelectItem value="6 Monate">3–6 Monate</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </Section>

        {/* 7. Lage & Besonderheiten - updated rating options */}
        <Section num="7" title="Lage & Besonderheiten">
          <Field label="Lageeinschaetzung">
            <ToggleGroup
              type="single"
              value={getDisplayValue(form["7_lage_einschaetzung"])}
              onValueChange={(v) => set("7_lage_einschaetzung", v)}
              className="justify-start gap-2 flex-wrap"
              data-testid="field-lage-group"
            >
{[
  { value: "sehr gut", label: "Sehr gut" },
  { value: "gut", label: "Gut" },
  { value: "mittel", label: "Mittel" },
  { value: "schlecht", label: "Schlecht" },
  { value: "katastrophal", label: "Katastrophal" },
].map((opt) => (
  <ToggleGroupItem
    key={opt.value}
    value={opt.value}
    className="px-4 py-2 text-sm rounded-md border border-[#E2E4E9] data-[state=on]:bg-[#0A4F53] data-[state=on]:text-white data-[state=on]:border-[#0A4F53] hover:bg-[#F8F9FA]"
    data-testid={`field-lage-${opt.value.replace(/\s/g, "-")}`}
  >
    {opt.label}
  </ToggleGroupItem>
              ))}

            </ToggleGroup>
          </Field>
          <Field label="Besonderheiten" htmlFor="besonderheiten">
            <Textarea
              id="besonderheiten"
              value={form["7_besonderheiten"]}
              onChange={(e) => set("7_besonderheiten", e.target.value)}
              placeholder="Besondere Merkmale, Lage-Details..."
              rows={3}
              data-testid="field-besonderheiten"
            />
          </Field>
        </Section>

        {/* 8. KO-Kriterium & Manuelle Entscheidung */}
        <Section num="8" title="KO-Kriterium & Entscheidung">
          <Field label="KO-Kriterium">
            <ToggleGroup
              type="single"
value={getDisplayValue(form["8_KO_kriterium"]) || ""}
onValueChange={(v) => {
  const current = getDisplayValue(form["8_KO_kriterium"]);

  if (!v || v === current) {
    set("8_KO_kriterium", "");
    return;
  }

  set("8_KO_kriterium", v);
}}
              className="justify-start gap-2 flex-wrap"
              data-testid="field-ko-group"
            >
{[
  { value: "Kein Verkaufsinteresse", label: "Kein Verkaufsinteresse" },
  { value: "Lage komplett unbrauchbar", label: "Objekt/Lage komplett unbrauchbar" },
  { value: "Preis völlig unrealistisch", label: "Preis völlig unrealistisch" },
].map((opt) => (
                <ToggleGroupItem
key={opt.value}
value={opt.value}
                  className="px-4 py-2 text-sm rounded-md border border-[#E2E4E9] data-[state=on]:bg-red-600 data-[state=on]:text-white data-[state=on]:border-red-600 hover:bg-red-50"
                  data-testid={`field-ko-${opt.value.toLowerCase().replace(/[\s/]/g, "-")}`}
                >
                  {opt.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </Field>
          {getDisplayValue(form["8_KO_kriterium"]) && (
            <div
              className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-md flex items-center gap-2"
              data-testid="ko-warning"
            >
              <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
              <span className="text-sm font-medium text-red-700">
                Nicht weiterverfolgen – Score: 0%
              </span>
            </div>
          )}

          <div className="border-t border-[#E2E4E9] pt-5 mt-1">
            <Field label="Manuelle Entscheidung (überschreibt Score)">
              <div className={`${getDisplayValue(form.manuelle_entscheidung) ? "ring-2 ring-[#0A4F53]/30 rounded-lg p-2.5 bg-[#F0FAF9]" : ""}`}>
                <ToggleGroup
                  type="single"
		  key={getDisplayValue(form.manuelle_entscheidung) || "manual-decision-empty"}
		  value={getDisplayValue(form.manuelle_entscheidung) || ""}
		  onValueChange={(v) => {
  		    const current = getDisplayValue(form.manuelle_entscheidung);

  if (!v || v === current) {
    set("manuelle_entscheidung", "");
    set("9_status_ziel", "");
    return;
  }

  set("manuelle_entscheidung", v);
  set("9_status_ziel", v);
}}
                  className="justify-start gap-2 flex-wrap"
                  data-testid="field-manuelle-entscheidung-group"
                >
{[
  {
    value: "ablehnen",
    label: "Ablehnen",
    statusLabel: "abgelehnt",
    c: "data-[state=on]:bg-red-600 data-[state=on]:text-white data-[state=on]:border-red-600 hover:bg-red-50",
  },
  {
    value: "prüfen",
    label: "Prüfen",
    statusLabel: "prüfen",
    c: "data-[state=on]:bg-amber-500 data-[state=on]:text-white data-[state=on]:border-amber-500 hover:bg-amber-50",
  },
  {
    value: "besichtigung",
    label: "Besichtigung",
    statusLabel: "besichtigung terminiert",
    c: "data-[state=on]:bg-[#0A4F53] data-[state=on]:text-white data-[state=on]:border-[#0A4F53] hover:bg-[#F0FAF9]",
  },
].map((opt) => (
  <ToggleGroupItem
    key={opt.value}
    value={opt.value}
    className={`px-4 py-2 text-sm rounded-md border border-[#E2E4E9] ${opt.c}`}
    data-testid={`field-entscheidung-${opt.value}`}
  >
    {opt.label}
  </ToggleGroupItem>
))}
</ToggleGroup>
{getDisplayValue(form.manuelle_entscheidung) && (
  <p
    className="text-xs text-[#0A4F53] font-medium mt-2 flex items-center gap-1"
    data-testid="entscheidung-active-hint"
  >
    Aktiv: Workflow wird auf „{
      getDisplayValue(form.manuelle_entscheidung) === "ablehnen"
        ? "abgelehnt"
        : getDisplayValue(form.manuelle_entscheidung) === "prüfen"
        ? "prüfen"
        : "besichtigung terminiert"
    }“ gesetzt
  </p>
)}

              </div>
            </Field>
          </div>
        </Section>

        {/* 9. Abschluss - with date + time picker */}
        <Section num="9" title="Abschluss" last>
          <Field label="Besichtigungstermin">
            <div className="flex gap-3 items-start">
              <div className="flex-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal border-[#E2E4E9]"
                      data-testid="field-besichtigungstermin-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-[#5E646A]" />
                      {form["9_besichtigungstermin"]
                        ? format(new Date(form["9_besichtigungstermin"]), "dd.MM.yyyy")
                        : "Datum auswaehlen..."}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={
                        form["9_besichtigungstermin"]
                          ? new Date(form["9_besichtigungstermin"])
                          : undefined
                      }
                      onSelect={(date) => {
                        if (date) {
                          set("9_besichtigungstermin", date.toISOString());
                        } else {
                          // Clearing the date also clears the companion time
                          // so n8n/Calendar/Telegram never see a half-state.
                          set("9_besichtigungstermin", null);
                          set("9_besichtigungstermin_zeit", "");
                        }
                      }}
                      data-testid="field-besichtigungstermin-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex gap-1.5 items-center shrink-0">
                <Select
                  value={form["9_besichtigungstermin_zeit"]?.split(":")[0] || ""}
                  onValueChange={(h) => {
                    const m = form["9_besichtigungstermin_zeit"]?.split(":")[1] || "00";
                    set("9_besichtigungstermin_zeit", `${h}:${m}`);
                  }}
                >
                  <SelectTrigger className="w-[72px]" data-testid="field-termin-hour">
                    <SelectValue placeholder="Std" />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map((h) => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-[#5E646A] font-medium">:</span>
                <Select
                  value={form["9_besichtigungstermin_zeit"]?.split(":")[1] || ""}
                  onValueChange={(m) => {
                    const h = form["9_besichtigungstermin_zeit"]?.split(":")[0] || "10";
                    set("9_besichtigungstermin_zeit", `${h}:${m}`);
                  }}
                >
                  <SelectTrigger className="w-[72px]" data-testid="field-termin-minute">
                    <SelectValue placeholder="Min" />
                  </SelectTrigger>
                  <SelectContent>
                    {MINUTES.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-[#5E646A] ml-1">Uhr</span>
              </div>
            </div>
            {form["9_besichtigungstermin"] && form["9_besichtigungstermin_zeit"] && (
              <p className="text-xs text-[#0A4F53] font-medium mt-2" data-testid="termin-preview">
                Termin: {format(new Date(form["9_besichtigungstermin"]), "dd.MM.yyyy")} um {form["9_besichtigungstermin_zeit"]} Uhr
              </p>
            )}
          </Field>
          <div className="flex items-center gap-3">
            <Checkbox
              id="nachkontakt"
              checked={form["9_nachkontakt_abgeschlossen"]}
              onCheckedChange={(v) =>
                set("9_nachkontakt_abgeschlossen", Boolean(v))
              }
              data-testid="field-nachkontakt"
            />
            <Label
              htmlFor="nachkontakt"
              className="text-sm font-medium text-[#1C1F22] cursor-pointer"
            >
              Nachkontakt abgeschlossen
            </Label>
          </div>
          <Field label="Nachkontakt Notiz" htmlFor="nachkontakt_notiz">
            <Textarea
              id="nachkontakt_notiz"
              value={form["9_nachkontakt_notiz"]}
              onChange={(e) => set("9_nachkontakt_notiz", e.target.value)}
              placeholder="Notizen zum Nachkontakt..."
              rows={3}
              data-testid="field-nachkontakt-notiz"
            />
          </Field>
        </Section>
      </div>

      {/* Sticky Bottom Bar */}
      <div className="border-t border-[#E2E4E9] bg-[#F8F9FA] px-6 py-4 flex items-center justify-between shrink-0">
        <p className="text-xs text-[#8A9198]">
          Aenderungen werden erst beim Speichern uebernommen
        </p>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => onSave(getPayload())}
            disabled={saving}
            className="border-[#E2E4E9] text-[#1C1F22] bg-white hover:bg-[#F0F2F5]"
            data-testid="save-button"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Speichert..." : "Zwischenspeichern"}
          </Button>
          <Button
            onClick={() => onComplete(getPayload())}
            disabled={saving}
            className="bg-[#0A4F53] hover:bg-[#073B3E] text-white"
            data-testid="complete-button"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Nachkontakt abschliessen
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ num, title, children, last }) {
  return (
    <div
      className={`mb-6 rounded-lg border border-[#E2E4E9] bg-white shadow-sm overflow-hidden ${last ? "" : ""}`}
    >
      <div className="border-l-[3px] border-l-[#0A4F53] px-6 py-5">
        <h3 className="text-sm font-bold text-[#0A4F53] uppercase tracking-widest mb-5 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-[#0A4F53] text-white text-[10px] font-bold">
            {num}
          </span>
          {title}
        </h3>
        <div className="space-y-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <Label
        htmlFor={htmlFor}
        className="text-xs font-semibold text-[#5E646A] uppercase tracking-wider mb-1.5 block"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}
